import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import {
  Lock,
  Shield,
  KeyRound,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';

export const AccountSecurityTab: React.FC = () => {
  const { changePassword } = useAuth();
  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      showToast({
        type: 'success',
        title: 'Password Updated',
        message: 'Your account password was updated successfully.',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err?.message || 'Current password incorrect or server error.');
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Failed to change password.',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card padding="lg" className="space-y-4">
        <h2 className="text-sm font-semibold text-cf-textPrimary flex items-center gap-2">
          <Lock size={16} className="text-cf-orange" />
          <span>Change Account Password</span>
        </h2>

        <p className="text-xs text-cf-textSecondary">
          Ensure your account is protected with a strong password (minimum 8 characters). Passwords are securely hashed with bcrypt.
        </p>

        {passwordError && (
          <div className="p-2.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 text-xs flex items-center gap-2">
            <Shield size={14} className="flex-shrink-0" />
            <span>{passwordError}</span>
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Current Password"
              type="password"
              required
              size="sm"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
            />
            <Input
              label="New Password"
              type="password"
              required
              minLength={8}
              size="sm"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters"
            />
            <Input
              label="Confirm New Password"
              type="password"
              required
              minLength={8}
              size="sm"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
              error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
            />
          </div>

          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              variant="primary"
              size="xs"
              disabled={isChangingPassword || !currentPassword || !newPassword}
              loading={isChangingPassword}
              icon={<KeyRound size={13} />}
            >
              Update Password
            </Button>
          </div>
        </form>
      </Card>

      <Card padding="lg" className="space-y-4">
        <h3 className="text-sm font-semibold text-cf-textPrimary flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-500 dark:text-emerald-400" />
          <span>Authentication & Session Security</span>
        </h3>
        <p className="text-xs text-cf-textSecondary">
          Your session is authenticated via cryptographically generated tokens signed with SHA-256 hashes. Sessions expire after 30 days of inactivity.
        </p>
        <div className="p-3 bg-cf-bg border border-cf-border rounded-lg text-xs text-cf-textSecondary flex items-center justify-between">
          <div>
            <span className="text-cf-textPrimary font-semibold block">Active Web Session</span>
            <span className="text-xs text-cf-textMuted">Prefix: erc_live_sess_ • Rolling 30-day token</span>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs font-semibold">
            <CheckCircle2 size={11} /> SECURE
          </span>
        </div>
      </Card>
    </div>
  );
};
